import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const DEFAULT_AFFILIATE_PERCENT = 30;
const DEFAULT_AFFILIATE_DURATION = 12;
const USERS_PER_PAGE = 20;

const formatCurrency = (amount) =>
  `${(Number(amount) || 0).toLocaleString("vi-VN")}đ`;

const buildAdminHeaders = (withJson = false) => {
  const token = localStorage.getItem("userAccessToken");
  const adminPassword =
    process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
  return {
    ...(withJson ? { "Content-Type": "application/json" } : {}),
    ...(adminPassword ? { "X-Admin-Password": adminPassword } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const AdminAffiliateManagement = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
  const [affiliates, setAffiliates] = useState([]);
  const [filteredAffiliates, setFilteredAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "totalEarnings",
    direction: "descending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);
  const [systemConfigLoading, setSystemConfigLoading] = useState(true);
  const [systemConfigSaving, setSystemConfigSaving] = useState(false);
  const [editSystemPercent, setEditSystemPercent] = useState("");
  const [editSystemDuration, setEditSystemDuration] = useState("");
  const [showSystemConfigEdit, setShowSystemConfigEdit] = useState(false);

  useEffect(() => {
    const fetchAffiliateUsers = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          limit: "200",
        });
        const response = await fetch(
          `${API_URL}/api/admin/affiliates?${query.toString()}`,
          {
            headers: buildAdminHeaders(),
          }
        );
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || "Không thể tải danh sách affiliate");
        }

        const affiliateUsers = (result.data || []).map((row) => ({
          ...row,
          affiliatePercent:
            Number(row.affiliatePercent) || DEFAULT_AFFILIATE_PERCENT,
          affiliateDuration:
            Number(row.affiliateDuration) || DEFAULT_AFFILIATE_DURATION,
          totalEarnings: Number(row.totalEarnings) || 0,
          totalSales: Number(row.totalSales) || 0,
          completedSignups: Number(row.completedSignups) || 0,
          pendingSignups: Number(row.pendingSignups) || 0,
          signups: row.signups || {},
          signupCount: Number(row.signupCount) || 0,
        }));

        setAffiliates(affiliateUsers);
        setFilteredAffiliates(affiliateUsers);
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu affiliate admin:", error);
        setAffiliates([]);
        setFilteredAffiliates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliateUsers();
  }, []);

  useEffect(() => {
    const fetchSystemConfig = async () => {
      setSystemConfigLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/admin/affiliate-config`,
          { headers: buildAdminHeaders() }
        );
        const result = await response.json();
        if (result.success && result.data) {
          setSystemConfig(result.data);
          setEditSystemPercent(
            result.data.defaultPercent ?? DEFAULT_AFFILIATE_PERCENT
          );
          setEditSystemDuration(
            result.data.defaultDuration ?? DEFAULT_AFFILIATE_DURATION
          );
        }
      } catch (error) {
        console.error("Lỗi khi tải cấu hình affiliate hệ thống:", error);
      } finally {
        setSystemConfigLoading(false);
      }
    };

    fetchSystemConfig();
  }, []);

  useEffect(() => {
    const keyword = searchText.trim().toLowerCase();
    let nextAffiliates = [...affiliates];

    if (keyword) {
      nextAffiliates = nextAffiliates.filter((affiliate) => {
        return (
          affiliate.id.toLowerCase().includes(keyword) ||
          affiliate.name.toLowerCase().includes(keyword) ||
          affiliate.email.toLowerCase().includes(keyword) ||
          affiliate.phone.toLowerCase().includes(keyword)
        );
      });
    }

    nextAffiliates.sort((left, right) => {
      const leftValue = left[sortConfig.key] || 0;
      const rightValue = right[sortConfig.key] || 0;

      if (typeof leftValue === "string" || typeof rightValue === "string") {
        if (sortConfig.direction === "ascending") {
          return String(leftValue).localeCompare(String(rightValue), "vi");
        }

        return String(rightValue).localeCompare(String(leftValue), "vi");
      }

      if (sortConfig.direction === "ascending") {
        return leftValue - rightValue;
      }

      return rightValue - leftValue;
    });

    setFilteredAffiliates(nextAffiliates);
    setCurrentPage(1);
  }, [affiliates, searchText, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "descending"
          ? "ascending"
          : "descending",
    }));
  };

  const handleCopyAffiliateLink = async (affiliateId) => {
    const link = `${window.location.origin}/?ref=${affiliateId}`;
    await navigator.clipboard.writeText(link);
    setCopiedLinkId(affiliateId);
    window.setTimeout(() => setCopiedLinkId(null), 1500);
  };

  const handleOpenAffiliate = async (affiliate) => {
    setSelectedAffiliate({ ...affiliate });
    setEditMode(false);
    setShowModal(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${affiliate.id}`, {
        headers: buildAdminHeaders(),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSelectedAffiliate((prev) => ({
          ...prev,
          signups: result.data?.signups || {},
        }));
      }
    } catch (error) {
      console.error("Lỗi khi tải chi tiết affiliate:", error);
    }
  };

  const handleAffiliateInputChange = (event) => {
    const { name, value } = event.target;

    let nextValue = value;
    if (name === "affiliatePercent" || name === "affiliateDuration") {
      nextValue = value === "" ? "" : Number(value);
    }

    setSelectedAffiliate((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleSaveAffiliate = async () => {
    if (!selectedAffiliate) {
      return;
    }

    const affiliatePercent =
      selectedAffiliate.affiliatePercent === ""
        ? DEFAULT_AFFILIATE_PERCENT
        : Number(selectedAffiliate.affiliatePercent);
    const affiliateDuration =
      selectedAffiliate.affiliateDuration === ""
        ? DEFAULT_AFFILIATE_DURATION
        : Number(selectedAffiliate.affiliateDuration);

    if (
      !Number.isFinite(affiliatePercent) ||
      affiliatePercent < 0 ||
      affiliatePercent > 100
    ) {
      alert("Phần trăm hoa hồng phải nằm trong khoảng 0% đến 100%.");
      return;
    }

    if (!Number.isFinite(affiliateDuration) || affiliateDuration <= 0) {
      alert("Thời hạn affiliate phải lớn hơn 0 tháng.");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${selectedAffiliate.id}`,
        {
          method: "PATCH",
          headers: buildAdminHeaders(true),
          body: JSON.stringify({
            userData: {
              affiliatePercent,
              affiliateDuration,
            },
          }),
        }
      );
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Không thể cập nhật affiliate");
      }

      setAffiliates((prev) =>
        prev.map((affiliate) =>
          affiliate.id === selectedAffiliate.id
            ? {
                ...affiliate,
                affiliatePercent,
                affiliateDuration,
              }
            : affiliate
        )
      );

      setSelectedAffiliate((prev) => ({
        ...prev,
        affiliatePercent,
        affiliateDuration,
      }));
      setEditMode(false);
      alert("Đã cập nhật cấu hình affiliate thành công.");
    } catch (error) {
      console.error("Lỗi khi cập nhật cấu hình affiliate:", error);
      alert("Không thể cập nhật cấu hình affiliate.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenUserDetail = (affiliateId) => {
    localStorage.setItem("adminFocusUserId", affiliateId);
    navigate("/admin/users");
  };

  const handleSaveSystemConfig = async () => {
    const percent =
      editSystemPercent === ""
        ? DEFAULT_AFFILIATE_PERCENT
        : Number(editSystemPercent);
    const duration =
      editSystemDuration === ""
        ? DEFAULT_AFFILIATE_DURATION
        : Number(editSystemDuration);

    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      alert("Phần trăm hoa hồng phải nằm trong khoảng 0% đến 100%.");
      return;
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      alert("Thời hạn phải lớn hơn 0 tháng.");
      return;
    }

    setSystemConfigSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/affiliate-config`,
        {
          method: "PUT",
          headers: buildAdminHeaders(true),
          body: JSON.stringify({
            defaultPercent: percent,
            defaultDuration: duration,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Không thể cập nhật cấu hình");
      }

      setSystemConfig(result.data);
      setEditSystemPercent(result.data.defaultPercent ?? percent);
      setEditSystemDuration(result.data.defaultDuration ?? duration);
      setShowSystemConfigEdit(false);
      alert("Đã cập nhật cấu hình hệ thống. Chỉ áp dụng cho referral mới.");
    } catch (error) {
      console.error("Lỗi khi cập nhật cấu hình:", error);
      alert(error.message || "Không thể cập nhật cấu hình affiliate hệ thống.");
    } finally {
      setSystemConfigSaving(false);
    }
  };

  const activeAffiliates = filteredAffiliates.filter(
    (affiliate) =>
      affiliate.signupCount > 0 || affiliate.totalEarnings > 0 || affiliate.totalSales > 0
  );
  const totalAffiliateEarnings = filteredAffiliates.reduce(
    (sum, affiliate) => sum + affiliate.totalEarnings,
    0
  );
  const totalAffiliateSales = filteredAffiliates.reduce(
    (sum, affiliate) => sum + affiliate.totalSales,
    0
  );
  const averageAffiliatePercent = filteredAffiliates.length
    ? (
        filteredAffiliates.reduce(
          (sum, affiliate) => sum + affiliate.affiliatePercent,
          0
        ) / filteredAffiliates.length
      ).toFixed(1)
    : "0.0";
  const customPercentUsers = filteredAffiliates.filter(
    (affiliate) => affiliate.affiliatePercent !== DEFAULT_AFFILIATE_PERCENT
  ).length;

  const indexOfLastAffiliate = currentPage * USERS_PER_PAGE;
  const indexOfFirstAffiliate = indexOfLastAffiliate - USERS_PER_PAGE;
  const currentAffiliates = filteredAffiliates.slice(
    indexOfFirstAffiliate,
    indexOfLastAffiliate
  );
  const totalPages = Math.ceil(filteredAffiliates.length / USERS_PER_PAGE);

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    const pages = [];
    for (let page = 1; page <= totalPages; page += 1) {
      pages.push(
        <button
          key={page}
          type="button"
          onClick={() => setCurrentPage(page)}
          className={`px-3 py-1 rounded ${
            currentPage === page
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {page}
        </button>
      );
    }

    return <div className="flex flex-wrap gap-2 justify-center mt-4">{pages}</div>;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Quản lý affiliate
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Theo dõi cấu hình hoa hồng, doanh số và mở nhanh chi tiết từng user.
        </p>
      </div>

      {/* System Affiliate Config Panel */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Cấu hình hoa hồng hệ thống
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Áp dụng cho tất cả affiliate không có cấu hình riêng. Thay đổi chỉ ảnh hưởng referral mới.
            </p>
          </div>
          {!showSystemConfigEdit && (
            <button
              type="button"
              onClick={() => setShowSystemConfigEdit(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Chỉnh sửa
            </button>
          )}
        </div>
        <div className="px-6 py-4">
          {systemConfigLoading ? (
            <div className="text-gray-500 text-sm">Đang tải cấu hình...</div>
          ) : showSystemConfigEdit ? (
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tỷ lệ hoa hồng mặc định (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={editSystemPercent}
                  onChange={(e) => setEditSystemPercent(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Thời hạn mặc định (tháng)
                </label>
                <input
                  type="number"
                  min="1"
                  max="36"
                  step="1"
                  value={editSystemDuration}
                  onChange={(e) => setEditSystemDuration(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveSystemConfig}
                  disabled={systemConfigSaving}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {systemConfigSaving ? "Đang lưu..." : "Lưu"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSystemConfigEdit(false);
                    setEditSystemPercent(
                      systemConfig?.defaultPercent ?? DEFAULT_AFFILIATE_PERCENT
                    );
                    setEditSystemDuration(
                      systemConfig?.defaultDuration ?? DEFAULT_AFFILIATE_DURATION
                    );
                  }}
                  className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  Hủy
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-6">
              <div>
                <div className="text-sm text-gray-500">Tỷ lệ hoa hồng</div>
                <div className="text-xl font-bold text-blue-600">
                  {systemConfig?.defaultPercent ?? DEFAULT_AFFILIATE_PERCENT}%
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Thời hạn</div>
                <div className="text-xl font-bold text-blue-600">
                  {systemConfig?.defaultDuration ?? DEFAULT_AFFILIATE_DURATION} tháng
                </div>
              </div>
              {systemConfig?.updatedAt && (
                <div>
                  <div className="text-sm text-gray-500">Cập nhật lần cuối</div>
                  <div className="text-sm text-gray-700">
                    {new Date(systemConfig.updatedAt).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" })}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              ⚠️ <strong>Lưu ý:</strong> Khi thay đổi tỷ lệ hoa hồng hệ thống, chỉ các referral <strong>mới</strong> sẽ áp dụng tỷ lệ mới.
              Referral cũ giữ nguyên tỷ lệ tại thời điểm đăng ký (snapshot). Để thay đổi tỷ lệ cho một user cụ thể, dùng nút "Sửa" trong bảng bên dưới.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Tổng user affiliate</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {filteredAffiliates.length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Affiliate có chuyển đổi</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {activeAffiliates.length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Tổng doanh số affiliate</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {formatCurrency(totalAffiliateSales)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Tổng hoa hồng</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {formatCurrency(totalAffiliateEarnings)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">TB % hoa hồng</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {averageAffiliatePercent}%
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {customPercentUsers} user dùng % tùy chỉnh
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex flex-col md:flex-row gap-4 md:items-end md:justify-between">
            <div className="flex-1">
              <label
                htmlFor="affiliate-search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tìm affiliate
              </label>
              <input
                id="affiliate-search"
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Tìm theo ID, tên, email, số điện thoại..."
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="text-sm text-gray-500">
              Đang hiển thị {currentAffiliates.length} / {filteredAffiliates.length} user
            </div>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-gray-600">Đang tải dữ liệu affiliate...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600 uppercase text-xs leading-normal">
                      <th className="py-3 px-4 text-left">User</th>
                      <th
                        className="py-3 px-4 text-left cursor-pointer"
                        onClick={() => handleSort("affiliatePercent")}
                      >
                        % hoa hồng
                      </th>
                      <th
                        className="py-3 px-4 text-left cursor-pointer"
                        onClick={() => handleSort("affiliateDuration")}
                      >
                        Thời hạn
                      </th>
                      <th
                        className="py-3 px-4 text-left cursor-pointer"
                        onClick={() => handleSort("completedSignups")}
                      >
                        Signup
                      </th>
                      <th
                        className="py-3 px-4 text-left cursor-pointer"
                        onClick={() => handleSort("totalSales")}
                      >
                        Doanh số
                      </th>
                      <th
                        className="py-3 px-4 text-left cursor-pointer"
                        onClick={() => handleSort("totalEarnings")}
                      >
                        Hoa hồng
                      </th>
                      <th className="py-3 px-4 text-center">Hành động</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600 text-sm">
                    {currentAffiliates.length > 0 ? (
                      currentAffiliates.map((affiliate) => (
                        <tr
                          key={affiliate.id}
                          className="border-b border-gray-200 hover:bg-gray-50"
                        >
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">
                              {affiliate.name || affiliate.id}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                              {affiliate.email || affiliate.id}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-blue-700">
                              {affiliate.affiliatePercent}%
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {affiliate.affiliateDuration} tháng
                          </td>
                          <td className="py-3 px-4">
                            {affiliate.completedSignups} hoàn tất
                            <div className="text-xs text-gray-500">
                              {affiliate.pendingSignups} chờ xử lý
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {formatCurrency(affiliate.totalSales)}
                          </td>
                          <td className="py-3 px-4">
                            {formatCurrency(affiliate.totalEarnings)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleOpenAffiliate(affiliate)}
                                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs"
                              >
                                Quản lý
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCopyAffiliateLink(affiliate.id)}
                                className="px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 text-xs"
                              >
                                {copiedLinkId === affiliate.id ? "Đã copy" : "Copy link"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-gray-500">
                          Không tìm thấy affiliate nào phù hợp.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {showModal && selectedAffiliate && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-lg max-w-3xl w-full mx-auto max-h-[90vh] overflow-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {editMode
                    ? "Chỉnh cấu hình affiliate"
                    : "Chi tiết affiliate"}
                </h3>
                <p className="text-sm text-gray-500 mt-1 break-all">
                  {selectedAffiliate.name || selectedAffiliate.id}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditMode(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User ID
                  </label>
                  <input
                    type="text"
                    value={selectedAffiliate.id}
                    disabled
                    className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="text"
                    value={selectedAffiliate.email || ""}
                    disabled
                    className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    % hoa hồng
                  </label>
                  <input
                    type="number"
                    name="affiliatePercent"
                    min="0"
                    max="100"
                    step="1"
                    value={selectedAffiliate.affiliatePercent}
                    onChange={handleAffiliateInputChange}
                    disabled={!editMode}
                    className={`w-full px-3 py-2 border rounded-md ${
                      editMode
                        ? "focus:ring-blue-500 focus:border-blue-500"
                        : "bg-gray-50 text-gray-500"
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thời hạn affiliate
                  </label>
                  <input
                    type="number"
                    name="affiliateDuration"
                    min="1"
                    step="1"
                    value={selectedAffiliate.affiliateDuration}
                    onChange={handleAffiliateInputChange}
                    disabled={!editMode}
                    className={`w-full px-3 py-2 border rounded-md ${
                      editMode
                        ? "focus:ring-blue-500 focus:border-blue-500"
                        : "bg-gray-50 text-gray-500"
                    }`}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Đơn vị: tháng
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-xs text-blue-600 uppercase tracking-wide">
                    Tổng signup
                  </div>
                  <div className="text-2xl font-bold text-blue-900 mt-2">
                    {selectedAffiliate.completedSignups}
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-xs text-yellow-600 uppercase tracking-wide">
                    Pending signup
                  </div>
                  <div className="text-2xl font-bold text-yellow-900 mt-2">
                    {selectedAffiliate.pendingSignups}
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <div className="text-xs text-emerald-600 uppercase tracking-wide">
                    Tổng doanh số
                  </div>
                  <div className="text-2xl font-bold text-emerald-900 mt-2">
                    {formatCurrency(selectedAffiliate.totalSales)}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-xs text-purple-600 uppercase tracking-wide">
                    Tổng hoa hồng
                  </div>
                  <div className="text-2xl font-bold text-purple-900 mt-2">
                    {formatCurrency(selectedAffiliate.totalEarnings)}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-800">
                    Link affiliate
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleCopyAffiliateLink(selectedAffiliate.id)}
                    className="px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 text-xs"
                  >
                    {copiedLinkId === selectedAffiliate.id ? "Đã copy" : "Copy link"}
                  </button>
                </div>
                <input
                  type="text"
                  value={`${window.location.origin}/?ref=${selectedAffiliate.id}`}
                  disabled
                  className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-500"
                />
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">
                  Signup gần đây
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-2 text-left">User</th>
                        <th className="px-4 py-2 text-left">Trạng thái</th>
                        <th className="px-4 py-2 text-left">Nguồn</th>
                        <th className="px-4 py-2 text-left">Thời gian</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedAffiliate.signups || {})
                        .sort(
                          (left, right) =>
                            (right[1]?.completedTimestamp ||
                              right[1]?.timestamp ||
                              0) -
                            (left[1]?.completedTimestamp ||
                              left[1]?.timestamp ||
                              0)
                        )
                        .slice(0, 10)
                        .map(([signupUserId, signup]) => (
                          <tr key={signupUserId} className="border-t">
                            <td className="px-4 py-2 break-all">
                              {signupUserId}
                            </td>
                            <td className="px-4 py-2">
                              {signup?.status || "unknown"}
                            </td>
                            <td className="px-4 py-2">
                              {signup?.source || "-"}
                            </td>
                            <td className="px-4 py-2">
                              {signup?.completedTime || signup?.time || "-"}
                            </td>
                          </tr>
                        ))}
                      {Object.keys(selectedAffiliate.signups || {}).length === 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-6 text-center text-gray-500">
                            Chưa có signup nào.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap justify-end gap-2">
              {editMode ? (
                <>
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    disabled={saving}
                  >
                    Hủy
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAffiliate}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={saving}
                  >
                    {saving ? "Đang lưu..." : "Lưu thay đổi"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleOpenUserDetail(selectedAffiliate.id)}
                    className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
                  >
                    Mở chi tiết user
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Chỉnh cấu hình
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Đóng
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliateManagement;
